import React from 'react';

import {
  AttachmentStatus,
  AttachmentErrorCode
} from 'widget/stores/AttachmentsStore';
import type { Attachment } from 'widget/stores/AttachmentsStore';

import { EditableAttachmentCard } from '../../EditableAttachmentCard';

export type AttachmentItemProps = {
  attachment: Attachment;
  onRemove: (id: string) => void;
  onRetry?: (id: string) => void;
};

const isRetryableError = (errorCode?: AttachmentErrorCode): boolean => {
  return errorCode === AttachmentErrorCode.uploadFailed;
};

const AttachmentItem: React.FC<AttachmentItemProps> = ({
  attachment,
  onRemove,
  onRetry
}) => {
  const isError = attachment.status === AttachmentStatus.ERROR;
  const isLoading = attachment.status === AttachmentStatus.UPLOADING;
  const canRetry = isError && isRetryableError(attachment.errorCode);

  const handleRemove = React.useCallback(() => {
    onRemove(attachment.id);
  }, [onRemove, attachment.id]);

  const handleRetry = React.useCallback(() => {
    onRetry?.(attachment.id);
  }, [onRetry, attachment.id]);

  const isVideo = attachment.mimeType.startsWith('video/');

  return (
    <EditableAttachmentCard
      filename={attachment.name}
      size={attachment.size}
      previewUrl={attachment.previewUrl}
      isVideo={isVideo}
      onRemove={handleRemove}
      onRetry={canRetry && onRetry ? handleRetry : undefined}
      isError={isError}
      isLoading={isLoading}
      errorText={isError ? attachment.error : undefined}
    />
  );
};

export default React.memo(AttachmentItem);
